import { useCallback, useEffect, useMemo } from 'react';
import cn from 'clsx';
import { getDefaultSortingOptionId, isNonEmptyObject } from '@pkgs/components/utils/searchStateUtils';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { usePromptStore } from '@pkgs/components/hooks/stores/search/usePromptStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import { SortingOptionId } from '@pkgs/components/enums/enums';
import IconSorting from '@pkgs/components/assets/icons/small/Sorting.svg';
import AiStarIcon from '@pkgs/components/assets/icons/small/AiStar.svg';
import { BusinessArea } from '@ev-tech/search-modules-api';

import { Skeleton } from '../Skeleton/Skeleton';
import { type SelectItem } from '../Select/Select.types';
import { Select } from '../Select/Select';
import { type WithTestId } from '../../types/types';
import { useAppContext } from '../../hooks/useAppContext';
import { useListings } from '../../hooks/queries/useListings';
import { SORT_BY_OPTIONS } from '../../consts/consts';

import styles from './SortBySelect.module.css';

type SortByItem = Omit<(typeof SORT_BY_OPTIONS)[number], 'label'> &
  SelectItem & {
    label: string;
  };

export const SortBySelect = ({ 'data-testid': dataTestId }: WithTestId = {}) => {
  const { t, autogenConfig, featureFlags } = useAppContext();

  const { isFetching: isListingsFetching } = useListings();
  const { isPending: isAiSearchFetching } = useAiSearch();
  const isFetching = isListingsFetching || isAiSearchFetching;
  const objectTypeCategory = useSearchStore((state) => state.objectTypeCategory);
  const propertyMarketingType = useSearchStore((state) => state.marketingType);
  const sortingOptionId = useSearchStore((state) => state.sortingOptionId);
  const setSortingOptionsById = useSearchStore((state) => state.setSortingOptionById);
  const promptValue = usePromptStore((state) => state.promptValue);
  const searchMode = useSearchStore((state) => state.searchMode);
  const { isMobile } = useBreakpoint();
  const businessArea = objectTypeCategory === 'development' ? BusinessArea.residential : objectTypeCategory;
  const preferences = useSearchStore((state) => state.preferences);
  const hasPerformedAiSearch = promptValue.trim().length > 0;
  const hasActiveAiSearchResults = searchMode === SearchMode.AI && hasPerformedAiSearch;

  useEffect(() => {
    if (isAiSearchFetching || !hasActiveAiSearchResults || sortingOptionId !== SortingOptionId.SMART_SORT) {
      return;
    }

    setSortingOptionsById(isNonEmptyObject(preferences) ? SortingOptionId.PREFERENCES : getDefaultSortingOptionId(propertyMarketingType));
  }, [hasActiveAiSearchResults, isAiSearchFetching, preferences, propertyMarketingType, setSortingOptionsById, sortingOptionId]);

  const sortByItems = useMemo(
    () =>
      SORT_BY_OPTIONS.reduce<SortByItem[]>((acc, item) => {
        if (item.id === SortingOptionId.SMART_SORT) {
          if (featureFlags.isSmartSortEnabled && !hasActiveAiSearchResults) {
            return [
              ...acc,
              {
                ...item,
                label: t(item.label),
              },
            ];
          } else {
            return acc;
          }
        }
        if (item.id === SortingOptionId.PREFERENCES) {
          if (hasActiveAiSearchResults && isNonEmptyObject(preferences)) {
            return [
              ...acc,
              {
                ...item,
                label: t(item.label),
                icon: <AiStarIcon />,
              },
            ];
          }
        } else {
          if (item.tags.includes(businessArea) && item.tags.includes(propertyMarketingType)) {
            return [
              ...acc,
              {
                ...item,
                label: t(item.label),
              },
            ];
          }
        }

        return acc;
      }, []),
    [businessArea, preferences, propertyMarketingType, t, featureFlags.isSmartSortEnabled, hasActiveAiSearchResults],
  );

  const selectOption = useMemo(
    () =>
      sortByItems.find((item) => {
        return item.id === sortingOptionId;
      }) ?? sortByItems[0],
    [sortingOptionId, sortByItems],
  );

  const handleSelectionChange = useCallback(
    (sortId: string | null) => {
      if (!sortId) return;
      const sortItem = sortByItems.find((item) => item.id === sortId);
      if (!sortItem) return;

      setSortingOptionsById(sortItem.id);
    },
    [setSortingOptionsById, sortByItems],
  );

  return (
    <Skeleton loading={isFetching}>
      <div className={cn(styles.container)} data-testid={dataTestId}>
        {!autogenConfig && <div className={styles.divider} />}
        <Select
          buttonIcon={<IconSorting />}
          items={sortByItems}
          value={selectOption?.id}
          buttonLabelOverride={isMobile ? t('search.searchbar.sortBy.label') : undefined}
          placeholder={isMobile ? t('search.searchbar.sortBy.label') : selectOption?.id}
          onChange={handleSelectionChange}
          side="bottom"
          align="end"
          variant="basicLight"
          hasLabelHidden
          shouldHideArrowIcon
          accessibilityLabel={t('search.select.hiddenAccessibilityText', {
            label: t('search.searchbar.sortBy.label'),
          })}
          label={t('search.searchbar.sortBy.label')}
        />
      </div>
    </Skeleton>
  );
};
